<template>
  <v-container
    fluid
  >
    <v-row
      class="ma-0"
    >
      <v-card
        width="100%"
      >
        <v-card-text>
          <v-row
            class="ma-0"
          >
            <v-col
              col="12"
              sm="12"
              md="7"
              lg="7"
              xl="7"
            >
              <v-row class="ma-0">
                <v-col
                  cols="12"
                >
                  <v-text-field
                    v-model="number"
                    label="Número do cartão"
                    v-mask="numberMasks"
                    append-icon="mdi-credit-card"
                    autocomplete="off"
                    :error-messages="numberErrors"
                    outlined
                  />
                </v-col>
                <v-col
                  cols="5"
                >
                  <v-select
                    v-model="month"
                    label="Mês de vencimento"
                    append-icon="mdi-calendar"
                    :items="months"
                    :error-messages="monthErrors"
                    outlined
                  />
                </v-col>
                <v-col
                  cols="5"
                >
                  <v-select
                    v-model="year"
                    label="Ano de vencimento"
                    append-icon="mdi-calendar"
                    :items="years"
                    :error-messages="yearErrors"
                    outlined
                  />
                </v-col>
                <v-col
                  cols="2"
                >
                  <v-text-field
                    v-model="cvv"
                    label="CVV"
                    @focus="cvvFocus = true"
                    @blur="cvvFocus = false"
                    :error-messages="cvvErrors"
                    v-mask="cvvMasks"
                    outlined
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col
              v-if="$vuetify.breakpoint.mdAndUp"
              md="5"
              lg="5"
              xl="5"
            >
              <credit-card-helper
                :cvv-focus="cvvFocus"
                :number="number"
                :cvv="cvv"
                :month="month"
                :year="year"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-row>
  </v-container>
</template>

<script>
  import {validationMixin} from "vuelidate"
  import CreditCardHelper from "./CreditCardHelper"
  import maxLength from "vuelidate/lib/validators/maxLength"
  import required from "vuelidate/lib/validators/required"
  import minLength from "vuelidate/lib/validators/minLength"
  import {guessCreditCardBrand, luhnCheck, unmask} from "../../../helpers/utils"
  import integer from "vuelidate/lib/validators/integer"
  import {NOTIFICATION_TYPES, NotificationOptions} from "../../../helpers/notify"
  import {cloneDeep, isEqual} from "lodash/lang"
  import {CREDIT_CARD_BRAND} from "../../../helpers/payment/constants"

  const SetResumeCardData = Symbol.for("SetResumeCardData")

  export default {
    name: "CreditCardData",
    mixins: [validationMixin],
    inject: {SetResumeCardData},
    components: {CreditCardHelper},
    model: {
      prop: "model",
      event: "change"
    },
    data: () => ({
      isLoading: false,

      cvvFocus: false,

      number: null,
      cvv: null,
      month: null,
      year: null,
    }),
    validations() {
      return {
        number: {
          required,
          maxLength: maxLength(19),
          minLength: minLength(18),
          isValid: function () {
            return luhnCheck(unmask(this.number, this.numberMasks))
          }
        },
        cvv: {
          required,
          integer,
        },
        month: {
          required
        },
        year: {
          required
        }
      }
    },
    methods: {
      validateCardData() {
        this.$v.$touch()
        if (this.$v.$invalid) return

        this.getDirectToken()
      },

      getDirectToken() {
        const callb = (err, token) => {
          this.isLoading = false
          if (err) {
            this.onGetDirectTokenFailure(err)
          }
          const unmaskedNumber = unmask(this.number, this.numberMasks)
          const resumeCardData = {
            strLastDigits: unmaskedNumber.substr(unmaskedNumber.length - 4),
            strCcBrand: this.creditCardBrand
          }
          this.SetResumeCardData(resumeCardData)
          this.$emit("complete", {
            cdCcToken: token,
            strCcBrand: this.creditCardBrand,
          })
        }

        this.isLoading = true
        // noinspection JSUnresolvedVariable
        const payment = new window.Boacompra.PaymentMethod()

        let cardNumber = cloneDeep(this.number)
        cardNumber = unmask(cardNumber, this.numberMasks)
        const cardData = {
          creditCard: cardNumber,
          cvv: this.cvv,
          expiration: {
            month: this.month,
            year: this.year
          }
        }
        payment.getDirectToken(cardData, callb)
      },

      onGetDirectTokenFailure(error) {
        let notification = {
          notificationType: NOTIFICATION_TYPES.WARNING,
          message: "Erro ao validar cartão de crédito no BoaCompra.",
        }
        switch (error) {
          case "credit_card_internal_error":
            notification["message"] = `Erro ao salvar cartão em uma PCI, por favor verifique os dados e tente novamente.`
            break
          case "session_create_error":
            notification["message"] = `Erro ao obter sessão no BoaCompra.`
            break
          case "session_creating_in_progress":
            notification["notificationType"] = NOTIFICATION_TYPES.INFO
            notification["message"] = `O processo de obter sessão no BoaCompra ainda está em andamento.`
            break
          case "session_network_error":
            notification["message"] = `Erro ao obter conexão com o BoaCompra, verifique sua internet e tente novamente.`
            break
        }
        this.$notify(new NotificationOptions(notification))
      },
    },
    computed: {
      creditCardBrand() {
        if (!this.number) return null
        if (unmask(this.number, this.numberMasks).length === 0) return null
        return guessCreditCardBrand(unmask(this.number, this.numberMasks))
      },
      numberMasks() {
        return ["#### #### #### ####"]
      },
      numberErrors() {
        const errors = []
        if (!this.$v.number.$dirty) return errors
        !this.$v.number.required && errors.push("Esse campo é obrigatório")
        !this.$v.number.minLength && errors.push("O mínimo de digitos é 15")
        !this.$v.number.maxLength && errors.push("O máximo de digitos é 16")
        !this.$v.number.isValid && errors.push("Número de cartão inválido")
        return errors
      },
      cvvMasks() {
        if (isEqual(this.creditCardBrand, CREDIT_CARD_BRAND.AMEX))
          return "####"
        return "###"
      },
      cvvErrors() {
        const errors = []
        if (!this.$v.cvv.$dirty) return errors
        !this.$v.cvv.required && errors.push("Esse campo é obrigatório")
        !this.$v.cvv.integer && errors.push("É apenas permitido números")
        return errors
      },
      monthErrors() {
        const errors = []
        if (!this.$v.month.$dirty) return errors
        !this.$v.month.required && errors.push("Esse campo é obrigatório")
        return errors
      },
      yearErrors() {
        const errors = []
        if (!this.$v.year.$dirty) return errors
        !this.$v.year.required && errors.push("Esse campo é obrigatório")
        return errors
      },
      years() {
        const y = new Date().getFullYear()
        const yArray = Array.from({length: y - (y - 10)}, (value, index) => (y + index).toString())
        return yArray.sort((a, b) => a.localeCompare(b))
      },
      months() {
        return Array.from(Array(12), (value, index) => (index + 1).toString())
      },
    },
    watch: {
      number(newValue) {
        if (!newValue) return null
        if (unmask(newValue, this.numberMasks).length === 0) return null
        this.$emit("change", guessCreditCardBrand(unmask(newValue, this.numberMasks)))
      }
    }
  }
</script>

<style scoped>

</style>
